import { useMemo, useState } from "react";
import { Button } from "@/components/ui/Button";
import { formatAmount } from "@/utils/formatAmount";
import {
  useEmployeeExpenses,
  useLinkExpenseToRecurringCost,
  useMissingRecurringInvoices,
  useSkipRecurringMonth,
} from "@/features/finances/hooks/useFinances";
import type { MissingInvoiceRow } from "@/features/finances/api/finances";
import type { EmployeeExpense } from "@/features/finances/types";
import { MonthlyPeriodSelect } from "./MonthlyPeriodSelect";

/** Pull the first human-readable message out of a wrapped/unwrapped DRF error. */
function firstError(e: unknown): string | null {
  const data = (e as { response?: { data?: unknown } })?.response?.data as
    | { errors?: unknown }
    | undefined;
  const errors = (data?.errors ?? data) as unknown;
  if (!errors) return null;
  if (typeof errors === "string") return errors;
  if (typeof errors === "object") {
    const val = Object.values(errors as Record<string, unknown>)[0];
    if (Array.isArray(val)) return String(val[0]);
    if (typeof val === "string") return val;
  }
  return null;
}

function isBefore(period: { year: number; month: number }, from: { year: number; month: number }) {
  return period.year * 12 + period.month < from.year * 12 + from.month;
}

/** Pick one of the already-filed invoices and attach it to this cost + month. */
function PickInvoiceModal({
  cost,
  year,
  month,
  onClose,
}: {
  cost: MissingInvoiceRow;
  year: number;
  month: number;
  onClose: () => void;
}) {
  const [search, setSearch] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const { data: expenses = [], isLoading } = useEmployeeExpenses();
  const linkMutation = useLinkExpenseToRecurringCost();

  // The same guards the server applies — showing a row that is bound to 400 is worse
  // than not showing it at all. Currency is deliberately NOT a guard (the server
  // converts at the NBP rate when attaching), and neither is is_reimbursement: a
  // fixed cost paid from a private account is filed as a zwrot and still links.
  const candidates = useMemo(() => {
    const q = search.trim().toLowerCase();
    return expenses
      .filter(
        (e) =>
          !e.recurring_cost
          && e.expense_type === "invoice"
          && e.status !== "rejected",
      )
      .filter((e) => !q || `${e.description} ${e.employee_name} ${e.amount}`.toLowerCase().includes(q))
      .slice(0, 50);
  }, [expenses, search]);

  const attach = async (exp: EmployeeExpense) => {
    setErr(null);
    try {
      await linkMutation.mutateAsync({
        id: exp.id,
        recurring_cost: cost.id,
        period_year: year,
        period_month: month,
      });
      onClose();
    } catch (e) {
      setErr(firstError(e) ?? "Nie udało się podpiąć faktury.");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 w-full max-w-2xl max-h-[80vh] flex flex-col">
        <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
          Podepnij fakturę — {cost.description}
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
          Miesiąc: {String(month).padStart(2, "0")}/{year} · wybierz złożoną już fakturę kosztową
        </p>
        <input
          className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm mb-3 dark:bg-gray-900"
          placeholder="Szukaj po opisie, osobie, kwocie…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="flex-1 overflow-y-auto -mx-2 px-2">
          {isLoading ? (
            <p className="text-sm text-gray-400">Wczytywanie…</p>
          ) : candidates.length === 0 ? (
            <p className="text-sm text-gray-400">
              Brak faktur do podpięcia. Fakturę można też dodać z pliku w panelu kosztu stałego.
            </p>
          ) : (
            <table className="w-full text-sm">
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                {candidates.map((e) => (
                  <tr key={e.id}>
                    <td className="py-2 pr-2">
                      <span className="text-gray-800 dark:text-gray-200">{e.description}</span>
                      <span className="block text-[11px] text-gray-400">
                        {e.employee_name} · {new Date(e.created_at).toLocaleDateString("pl-PL")}
                      </span>
                    </td>
                    <td className="py-2 px-2 text-right whitespace-nowrap tabular-nums text-gray-700 dark:text-gray-300">
                      {formatAmount(e.amount)} {e.currency}
                      {e.currency !== "PLN" && e.amount_pln == null && (
                        <span className="block text-[11px] text-gray-400">
                          przeliczy się po kursie NBP
                        </span>
                      )}
                    </td>
                    <td className="py-2 pl-2 text-right">
                      <button
                        onClick={() => attach(e)}
                        disabled={linkMutation.isPending}
                        className="text-xs text-sky-700 dark:text-sky-400 hover:underline disabled:opacity-50"
                      >
                        Podepnij
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {err && <p className="text-xs text-red-600 mt-2">{err}</p>}
        <div className="mt-4">
          <Button size="sm" variant="secondary" onClick={onClose}>
            Zamknij
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Which recurring costs have no invoice attached for the chosen month.
 *
 *  A missing month is not a visible hole: the forecast quietly substitutes the
 *  average of the other invoices, so the month is settled with an ESTIMATE. This
 *  panel lists exactly those months and lets you close them — attach the invoice,
 *  or state that there was none (then the month counts as 0,00 zł).
 */
export function MissingRecurringInvoicesPanel() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [pickFor, setPickFor] = useState<MissingInvoiceRow | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const { data, isLoading } = useMissingRecurringInvoices({ year, month });
  const skipMutation = useSkipRecurringMonth();
  const rows = data?.data ?? [];
  const meta = data?.meta;
  const estimatedOnly = meta ? isBefore({ year, month }, meta.real_amount_from) : false;
  // Miesiąc, który się jeszcze nie zaczął, nie ma czego domykać — bez tego „komplet"
  // na przyszłym miesiącu czytałby się jak potwierdzenie, że wszystko wpłynęło.
  const isFuture = isBefore(
    { year: now.getFullYear(), month: now.getMonth() + 1 },
    { year, month },
  );

  const markNoInvoice = async (row: MissingInvoiceRow) => {
    const note = prompt(
      `„${row.description}" — dlaczego w ${String(month).padStart(2, "0")}/${year} nie było faktury? (opcjonalnie)`,
      "",
    );
    if (note === null) return;   // anulowane
    setErr(null);
    try {
      await skipMutation.mutateAsync({
        id: row.id,
        period_year: year,
        period_month: month,
        note: note.trim(),
      });
    } catch (e) {
      setErr(firstError(e) ?? "Nie udało się oznaczyć miesiąca.");
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-5 mb-4 border border-amber-200 dark:border-amber-900/40">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
          Koszty stałe bez faktury
        </h3>
        <span
          className={`text-xs font-semibold rounded-full px-2 py-0.5 ${
            isFuture
              ? "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300"
              : rows.length === 0
                ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
                : "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
          }`}
        >
          {isFuture
            ? "miesiąc jeszcze trwa"
            : rows.length === 0
              ? "komplet"
              : `${rows.length} do domknięcia`}
        </span>
        <div className="ml-auto">
          <MonthlyPeriodSelect
            year={year}
            month={month}
            onChange={(y, m) => { setYear(y); setMonth(m); }}
          />
        </div>
      </div>

      <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-3 leading-snug">
        {estimatedOnly ? (
          <>
            Ten miesiąc jest liczony ORIENTACYJNIE — kwotą średnią. Rozliczenie realnymi
            fakturami obowiązuje od {String(meta?.real_amount_from.month).padStart(2, "0")}/
            {meta?.real_amount_from.year}.
          </>
        ) : (
          <>
            Miesiąc rozliczany REALNIE: koszt bez faktury leci na średniej (orientacyjnie),
            dopóki go nie domkniesz — fakturą albo informacją, że jej nie było (0,00 zł).
          </>
        )}
      </p>

      {err && <p className="text-xs text-red-600 mb-2">{err}</p>}

      {isLoading ? (
        <p className="text-sm text-gray-400">Wczytywanie…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {isFuture
            ? "Ten miesiąc jeszcze się nie rozliczył — faktur nikt nie mógł jeszcze podpiąć."
            : "Wszystkie koszty stałe tego miesiąca mają fakturę albo są domknięte."}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] uppercase text-gray-400 text-left">
                <th className="py-1 pr-2 font-medium">Koszt stały</th>
                <th className="py-1 px-2 font-medium text-right">Kwota orientacyjna</th>
                <th className="py-1 pl-2 font-medium text-right">Domknij miesiąc</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
              {rows.map((row) => (
                <tr key={row.id}>
                  <td className="py-2 pr-2">
                    <span className="text-gray-800 dark:text-gray-200">{row.description}</span>
                    <span className="block text-[11px] text-gray-400">
                      {[row.company_name, row.project_name].filter(Boolean).join(" · ") || "bez przedsięwzięcia"}
                    </span>
                  </td>
                  <td className="py-2 px-2 text-right whitespace-nowrap tabular-nums text-gray-500 dark:text-gray-400">
                    {formatAmount(row.estimated_amount)} PLN
                  </td>
                  <td className="py-2 pl-2 text-right whitespace-nowrap">
                    <button
                      onClick={() => { setErr(null); setPickFor(row); }}
                      className="text-xs text-sky-700 dark:text-sky-400 hover:underline"
                    >
                      Podepnij fakturę
                    </button>
                    <span className="text-gray-300 mx-1.5">|</span>
                    <button
                      onClick={() => markNoInvoice(row)}
                      disabled={skipMutation.isPending}
                      className="text-xs text-gray-500 hover:text-gray-800 dark:hover:text-gray-200 hover:underline disabled:opacity-50"
                    >
                      Nie było faktury
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pickFor && (
        <PickInvoiceModal
          cost={pickFor}
          year={year}
          month={month}
          onClose={() => setPickFor(null)}
        />
      )}
    </div>
  );
}
